/**
 * Task detail (WO-P1) — pushed OVER the tabs (title "Task", with back).
 *
 * Shows a task's fields, a Complete action (completeTask), and a lightweight edit
 * (status / priority / due) via inline Selects + a due Field. Reached from the work
 * queue (kind 'task'). NEVER imports @smartbox/*. No raw style values.
 */

import { useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getTask,
  updateTask,
  completeTask,
} from '../../../src/api/tasks';
import {
  Screen,
  Text,
  Card,
  Button,
  Badge,
  Field,
  Select,
  priorityBadge,
  taskStatusBadge,
} from '../../../src/components/ui';
import {
  formatDateTime,
  formatDueRelative,
  parseDateTimeInput,
  isoToInputValue,
  extractErrorMessage,
  humanize,
} from '../../../src/lib/format';
import { theme } from '../../../src/theme';
import type { UpdateTaskRequest, Priority, TaskStatus } from '@beam/api-types';

const STATUS_OPTIONS: ReadonlyArray<{ value: TaskStatus; label: string }> = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'done', label: 'Done' },
  { value: 'cancelled', label: 'Cancelled' },
];
const PRIORITY_OPTIONS: ReadonlyArray<{ value: Priority; label: string }> = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

export default function TaskDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Edit form state (initialized lazily when editing starts).
  const [status, setStatus] = useState<TaskStatus>('open');
  const [priority, setPriority] = useState<Priority>('medium');
  const [due, setDue] = useState('');

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['tasks', id],
    queryFn: () => getTask(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

  const onUpdated = (updated: Parameters<typeof queryClient.setQueryData>[1]) => {
    queryClient.setQueryData(['tasks', id], updated);
    void queryClient.invalidateQueries({ queryKey: ['work-queue'] });
    setActionError(null);
  };

  const updateMutation = useMutation({
    mutationFn: (body: UpdateTaskRequest) => updateTask(id!, body),
    onSuccess: (updated) => {
      onUpdated(updated);
      setEditing(false);
    },
    onError: (err) => setActionError(extractErrorMessage(err)),
  });

  const completeMutation = useMutation({
    mutationFn: () => completeTask(id!),
    onSuccess: onUpdated,
    onError: (err) => setActionError(extractErrorMessage(err)),
  });

  const startEdit = () => {
    if (!data) return;
    setStatus(data.status as TaskStatus);
    setPriority(data.priority as Priority);
    setDue(isoToInputValue(data.due_at));
    setActionError(null);
    setEditing(true);
  };

  const saveEdit = () => {
    setActionError(null);
    let dueIso: string | null;
    try {
      dueIso = parseDateTimeInput(due);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Invalid due date.');
      return;
    }
    updateMutation.mutate({ status, priority, due_at: dueIso });
  };

  if (!id) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <Text variant="body" color="danger">
            No task ID provided.
          </Text>
        </View>
      </Screen>
    );
  }

  if (isLoading) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <ActivityIndicator
            size="large"
            color={theme.color.action.primary}
            accessibilityLabel="Loading task"
          />
        </View>
      </Screen>
    );
  }

  if (isError || !data) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <Card>
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
              {extractErrorMessage(error, 'Failed to load task.')}
            </Text>
            <Button
              title="Retry"
              variant="secondary"
              size="sm"
              fullWidth={false}
              onPress={() => refetch()}
            />
          </Card>
        </View>
      </Screen>
    );
  }

  const pri = priorityBadge(data.priority);
  const st = taskStatusBadge(data.status);
  const dueRel = formatDueRelative(data.due_at);
  const isDone = data.status === 'done' || data.status === 'cancelled';

  return (
    <Screen scroll>
      <View style={styles.statusRow}>
        <Text variant="title">{data.title}</Text>
        <Badge status={st.key} label={st.label} />
      </View>

      {data.description ? (
        <Card>
          <Text variant="body">{data.description}</Text>
        </Card>
      ) : null}

      <Card>
        <Row label="Type" value={humanize(data.task_type)} />
        <View style={styles.cardRow}>
          <Text variant="small" color="secondary">
            Priority
          </Text>
          <Badge status={pri.key} label={pri.label} />
        </View>
        <View style={styles.cardRow}>
          <Text variant="small" color="secondary">
            Due
          </Text>
          <Text variant="data" color={dueRel.overdue ? 'danger' : 'primary'}>
            {data.due_at ? formatDateTime(data.due_at) : '—'}
          </Text>
        </View>
        {data.is_system_generated ? (
          <Row label="Source" value="Auto-generated" />
        ) : null}
        {data.job_id ? <Row label="Job" value={data.job_id.slice(0, 8).toUpperCase()} /> : null}
      </Card>

      {editing ? (
        <Card>
          <Text variant="bodyStrong">Edit task</Text>
          <Select label="Status" options={STATUS_OPTIONS} value={status} onChange={setStatus} />
          <Select label="Priority" options={PRIORITY_OPTIONS} value={priority} onChange={setPriority} />
          <Field
            label="Due (YYYY-MM-DD HH:MM)"
            placeholder="2026-06-20 17:00"
            value={due}
            onChangeText={setDue}
            autoCapitalize="none"
            accessibilityLabel="Task due date and time"
          />
          {actionError ? (
            <Text variant="small" color="danger" accessibilityRole="alert">
              {actionError}
            </Text>
          ) : null}
          <View style={styles.actions}>
            <Button
              title="Save changes"
              onPress={saveEdit}
              loading={updateMutation.isPending}
              disabled={updateMutation.isPending}
            />
            <Button
              title="Cancel"
              variant="secondary"
              onPress={() => setEditing(false)}
              disabled={updateMutation.isPending}
            />
          </View>
        </Card>
      ) : (
        <>
          {actionError ? (
            <Card>
              <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
                {actionError}
              </Text>
            </Card>
          ) : null}
          <View style={styles.actions}>
            {!isDone ? (
              <Button
                title="Mark complete"
                onPress={() => completeMutation.mutate()}
                loading={completeMutation.isPending}
                disabled={completeMutation.isPending}
                accessibilityLabel="Mark task complete"
              />
            ) : null}
            <Button title="Edit" variant="secondary" onPress={startEdit} />
          </View>
        </>
      )}
    </Screen>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <View style={styles.cardRow}>
      <Text variant="small" color="secondary">
        {label}
      </Text>
      <Text variant="data">{value}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  centeredState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing.md,
    paddingHorizontal: theme.spacing['2xl'],
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.sm,
  },
  cardRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  actions: { gap: theme.spacing.sm },
});
